import { useMemo } from 'react';

import { useUrlNumber, useUrlParam, useUrlSearch } from '@/hooks/urlState';

import { type FilterField, useListFilters } from './useListFilters';

export const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const DEFAULT_LIMIT = 20;
/** A hand-edited `?limit=` never asks the backend for more than this. */
export const MAX_LIMIT = 200;

const NO_FIELDS: readonly FilterField[] = [];

/** For a list without status tabs. */
export const NO_TABS = ['ALL'] as const;

/**
 * URL-backed list state (tab, search, page, limit and the "Filter (N)" panel
 * values) so a filtered list is shareable and survives Back from a detail
 * page. Pass `fields` as a module-level constant.
 */
export function useListQuery<T extends string>(
  tabs: readonly T[],
  fallback: T,
  fields: readonly FilterField[] = NO_FIELDS
) {
  const [tab, setTabRaw] = useUrlParam<T>('tab', fallback, {
    allowed: tabs,
    resetPage: true,
    history: 'push',
  });
  const search = useUrlSearch('q', { resetPage: true, delay: 350 });
  const [page, setPage] = useUrlNumber('page', 1, { min: 1 });
  const [rawLimit, setLimit] = useUrlNumber('limit', DEFAULT_LIMIT, {
    min: 1,
    resetPage: true,
  });
  const limit = Math.min(MAX_LIMIT, rawLimit);
  const filters = useListFilters(fields);

  return useMemo(
    () => ({
      tab,
      setTab: setTabRaw,
      search,
      page,
      setPage,
      limit,
      setLimit,
      filters,
    }),
    [tab, setTabRaw, search, page, setPage, limit, setLimit, filters]
  );
}
